import { BAND_COUNT, type Band, type Energy, type Motivation } from '@hmawiw/shared';
import { useEffect, useRef, useState } from 'react';
import { useSingleColumn } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { dueForHorizon } from '../../lib/horizon';
import { haptic } from '../../native/platform';
import { useSettings } from '../../store/settings';
import { useCards } from '../../store/cards';
import { useUi } from '../../store/ui';
import controls from '../controls.module.css';
import { EnergyGlyph, Inbox } from '../icons';
import { Sheet } from '../Sheet/Sheet';
import styles from './QuickAdd.module.css';

const BANDS = Array.from({ length: BAND_COUNT }, (_, i) => i as Band);
const LEVELS: Energy[] = [1, 2, 3];

export function QuickAdd() {
  const t = useT();
  const { open, prefill } = useUi((s) => s.quickAdd);
  const close = useUi((s) => s.closeQuickAdd);
  return (
    <Sheet open={open} label={t.t('add.title')} onClose={close}>
      {open ? <Form initialTitle={prefill?.title ?? ''} initialNotes={prefill?.notes ?? ''} initialMotivation={prefill?.motivation} initialBand={prefill?.band} initialInbox={prefill?.inbox === true} shared={!!(prefill?.title || prefill?.notes)} /> : null}
    </Sheet>
  );
}

function Form({ initialTitle, initialNotes, initialMotivation, initialBand, initialInbox, shared }: { initialTitle: string; initialNotes: string; initialMotivation?: Motivation; initialBand?: Band; initialInbox: boolean; shared: boolean }) {
  const t = useT();
  const single = useSingleColumn();
  const column = useUi((s) => s.column);
  const close = useUi((s) => s.closeQuickAdd);
  const createCard = useCards((s) => s.createCard);
  const preset = useSettings((s) => s.bandPreset);
  const columnCount = useSettings((s) => s.columnCount);
  const COLUMNS = Array.from({ length: columnCount }, (_, i) => i as Motivation);
  const [title, setTitle] = useState(initialTitle);
  const [notes, setNotes] = useState(initialNotes);
  const [motivation, setMotivation] = useState<Motivation>(Math.min(columnCount - 1, initialMotivation ?? (single ? column : Math.floor(columnCount / 2))));
  const [inbox, setInbox] = useState(initialInbox);
  // In Horizon mode the chips name a due horizon; new cards start in Whenever, undated.
  const [band, setBand] = useState<Band>(initialBand ?? (preset === 'horizon' ? 2 : 1));
  const [energy, setEnergy] = useState<Energy>(2);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
  }, []);

  const submit = () => {
    if (!title.trim()) {
      input.current?.focus();
      return;
    }
    if (inbox) void createCard({ title, notes: notes || null, motivation: 1, band: 1, energy, inbox: true });
    else if (preset === 'horizon') void createCard({ title, notes: notes || null, motivation, band: 1, energy, dueAt: dueForHorizon(band, { dueAt: null }, Date.now()) });
    else void createCard({ title, notes: notes || null, motivation, band, energy });
    haptic('confirm');
    close();
  };

  return (
    <>
      <div className={styles.head}>
        <h2 className={`display ${styles.title}`}>{t.t('add.title')}</h2>
        {shared ? <span className={styles.shared}>{t.t('add.shared')}</span> : null}
      </div>
      <input
        ref={input}
        className={controls.field}
        value={title}
        placeholder={t.t('add.placeholder')}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        aria-label={t.t('sheet.title')}
        autoComplete="off"
      />
      {shared || notes ? <textarea className={controls.field} value={notes} placeholder={t.t('sheet.notes')} onChange={(e) => setNotes(e.target.value)} aria-label={t.t('sheet.notes')} /> : null}

      <div className={controls.group}>
        <div className={controls.label}>{t.t('add.column')}</div>
        <div className={controls.chips}>
          {COLUMNS.map((m) => (
            <button
              key={m}
              type="button"
              className={`${controls.chip}${m === motivation && !inbox ? '' : ` ${styles.narrow}`}`}
              aria-pressed={m === motivation && !inbox}
              aria-label={t.column(m)}
              onClick={() => {
                setMotivation(m);
                setInbox(false);
              }}
            >
              <span className={styles.dot} style={{ background: `var(--m${m})` }} />
              {m === motivation && !inbox ? t.column(m) : null}
            </button>
          ))}
          <button type="button" className={`${controls.chip}${inbox ? '' : ` ${styles.narrow}`}`} aria-pressed={inbox} aria-label={t.t('add.inbox')} onClick={() => setInbox(true)} data-add-inbox="">
            <Inbox size={16} />
            {inbox ? t.t('add.inbox') : null}
          </button>
        </div>
      </div>

      {inbox ? null : (
      <div className={controls.group}>
        <div className={controls.label}>{t.t('sheet.importance')}</div>
        <div className={controls.chips}>
          {BANDS.map((b) => (
            <button key={b} type="button" className={controls.chip} aria-pressed={b === band} onClick={() => setBand(b)}>
              {t.band(b)}
            </button>
          ))}
        </div>
      </div>
      )}

      <div className={controls.group}>
        <div className={controls.label}>{t.t('sheet.energy')}</div>
        <div className={controls.chips}>
          {LEVELS.map((level) => (
            <button key={level} type="button" className={controls.chip} aria-pressed={level === energy} onClick={() => setEnergy(level)}>
              <EnergyGlyph level={level} />
              {t.t(`energy.${level}`)}
            </button>
          ))}
        </div>
      </div>

      <div className={controls.footer}>
        <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={close}>
          {t.t('add.cancel')}
        </button>
        <button type="button" className={`${controls.btn} ${controls.primary}`} onClick={submit}>
          {inbox ? t.t('add.toInbox') : t.t('add.submit', { column: t.column(motivation) })}
        </button>
      </div>
    </>
  );
}
